import React from 'react';
import { LoginModeProvider } from '@wix/cli-auth';
import type { LoginMode } from '@wix/cli-core-definitions';
import type { UserInfo } from '@wix/cli-auth';
import { BiProvider } from '@wix/cli-telemetry-react';
import { render } from '@wix/cli-ui-kit';
import { createCommand } from '@commander-js/extra-typings';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import type { ErrorReporter } from '@wix/cli-error-reporting';
import type { Panorama } from '@wix/cli-panorama';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import { createBiLogger } from './bi/index.js';
import { InitHeadlessSiteCommand } from './components/InitHeadlessSiteCommand.js';

export const getHeadlessInitCommand = ({
  errorReporter,
  userInfo,
  loginMode,
  panorama,
  cliVersion,
  cliSessionId,
  targetParentFolder,
  aiAgentName,
}: {
  errorReporter: ErrorReporter;
  userInfo: UserInfo | null;
  loginMode?: LoginMode;
  panorama: Panorama;
  cliVersion: string;
  cliSessionId: string;
  targetParentFolder: string;
  aiAgentName?: string;
}) => {
  return createCommand('init')
    .description(
      'Initialize a new Wix project in one step. The command signs you in, creates the project, and saves the generated configuration files locally so you can start developing right away.'
    )
    .action(async (_, command) => {
      errorReporter.setContext('Command Info', {
        Arguments: command.args,
        Options: command.opts(),
      });

      const { biLogger, reportCommandStartEvent } = await createBiLogger({
        errorReporter,
        cliVersion,
        cliSessionId,
        userId: userInfo?.userId,
        aiAgentName,
        command: 'init-headless-site',
      });

      const commandStatus = reportCommandStartEvent(command);
      const transaction = createCommandTransaction(panorama, command);

      try {
        let createdProject: { msid: string; appId: string } | undefined;

        transaction.start();

        await render(
          <BiProvider value={biLogger}>
            <ErrorReporterProvider value={errorReporter}>
              <PanoramaProvider value={panorama}>
                <LoginModeProvider value={loginMode}>
                  <InitHeadlessSiteCommand
                    targetParentFolder={targetParentFolder}
                    onComplete={(result) => {
                      createdProject = result;
                    }}
                  />
                </LoginModeProvider>
              </PanoramaProvider>
            </ErrorReporterProvider>
          </BiProvider>
        );

        transaction.finish();
        commandStatus.succeed(createdProject);
      } catch (e) {
        commandStatus.failed(e);
        throw e;
      }
    });
};
